import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import ClientHeader from "~components/ClientInfo/ClientHeader";
import useCliente from "~fetchs/useCliente";
import { useConfigureFooter } from "~hooks/useHelpers";
import TrackMappingFilter from "../components/TrackMapping/TrackMappingFilter";
import MappedVsConsumed from "../components/TrackMapping/MappedVsConsumed";
import { addVolumesFaturados } from "../helpers/volumesFaturados";
import TrackingGroupCard from "../components/TrackMapping/TrackingGroupCard";
import FunnelMassDateButton from "../components/shared/FunnelMassDateButton";
import NegotiationUpdateModal from "../components/Negotiations/NegotiationUpdateModal";
import ModalAction from "~components/Modal/ModalAction";
import { ModalSuccess, ModalClosed, IconSuccess, WarningIcon2 } from "~misc/icons";
import { getFunilOptionsView } from "~services/GlobalServices";
import { initialPath } from "~misc/paths";
import useFunilObraGrupoProduto from "~fetchs/useFunilObraGrupoProduto";
import { db } from "~db/db";
import useFunilObraUnidadeMedida from "~fetchs/Funnel/useFunilObraUnidadeMedida";
import { fixObject, formatFunnelData, getDevice, resetFunnel } from "../helpers/funnelHelpers";
import { formatDateToCustomString } from "~pages/PriceSearchV2/utils/searchHelpers";
import useFunilStatus from "~fetchs/useFunilStatus";
import useFunnelUnsavedChanges from "../hooks/useFunnelUnsavedChanges";
import originControl from "~services/utils/originControl";
import { randomId } from "~pages/Funnel/utils";
import useRequisicoesOffline from "~fetchs/useRequisicoesOffline";
import MessageForm from "~components/Form/components/MessageForm";
import { deviceType, isDesktop, isTablet } from "react-device-detect";
import LoaderFull from "~components/Form/components/LoaderFull";
import { useLiveQuery } from "dexie-react-hooks";
import { getLocation } from "~services/utils/geoLocation";
import { getReturnRoute } from "../helpers/returnRoutes";
import { isInvalidNegotiatedVolume } from "../helpers/isInvalidNegotiatedVolume";
import { isPastNegotiationDate } from "../helpers/pastDate";
import { toggleSelectedId } from "../helpers/selection";
import useCheckIn from "~fetchs/Roterizacao/useCheckIn";

const orderOptions = getFunilOptionsView().filter(
  (option) => option.value !== "etapa"
);

const parseNegociacao = (str) => {
  const match = /^(\d{4})-(\d{2})$/.exec(str || "");
  if (!match) return Infinity;
  const [, year, month] = match.map(Number);
  if (month < 1 || month > 12) return Infinity;
  return year * 12 + month;
};

const compareNegociacao = (a, b) => {
  const dateA = parseNegociacao(a.dataNegociacao);
  const dateB = parseNegociacao(b.dataNegociacao);
  return dateA === dateB ? 0 : dateA < dateB ? -1 : 1;
};

const sortGroups = (groups, orderValue) => {
  const sorted = [...groups];

  switch(orderValue) {
    case "grupo_nome":
      sorted.sort((a, b) => a.title.localeCompare(b.title, "pt-BR"));
      break;
    case "proxima_negociacao":
      return sorted
        .map((group) => ({
          ...group,
          items: [...group.items].sort(compareNegociacao),
        }))
        .sort((a, b) => compareNegociacao(a.items[0] ?? {}, b.items[0] ?? {}));
    default:
      sorted.sort((a, b) => {
        if (a.ordem === 0 && b.ordem !== 0) return 1;
        if (b.ordem === 0 && a.ordem !== 0) return -1;

        return a.ordem - b.ordem;
      })
      break;
  }
  return sorted;
};

const FunnelTrackMapping = () => {
  const navigate = useNavigate();
  const { parameter: clienteId } = useParams();
  const { checkin } = useCheckIn();

  const [searchParams] = useSearchParams();
  const { search, state: navigationState } = useLocation();
  const origem = searchParams.get("origem");
  const isActiveVisit = Boolean(checkin && String(checkin.clienteId) === String(clienteId));
  const retorno = searchParams.get("retorno") || (isActiveVisit ? "roteirizacao" : null);
  const isDirectRedirect = new URLSearchParams(search).get("from") === "direto";
  const isDirectRoteirizacao = isDirectRedirect && retorno === "roteirizacao";                  
  const hasSavedVisitGroup = isDirectRoteirizacao && navigationState?.groupSaved === true;
  const { origens } = originControl();
  const completeVisitFunnel = useCallback(() => {
    setEditedRowIds(new Set());
    navigate(getReturnRoute({ retorno, clienteId }), { replace: true });
  }, [clienteId, retorno, navigate]);

  const { save } = useRequisicoesOffline();

  const backRouteFromDireto = isDirectRedirect
    ? (() => {
        try {
          const rotas = JSON.parse(sessionStorage.getItem("rotas")) || [];
          const currentPath = window.location.pathname;
          const prev = [...rotas].reverse().find((r) => r !== currentPath);
          return prev || null;
        } catch {
          return null;
        }
      })()
    : null;

  const { cliente } = useCliente(clienteId);
  const [filters, setFilters] = useState({ grupoProduto: null, orderBy: null });
  const [selectedIds, setSelectedIds] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [funnel, setFunnel] = useState([]);
  const [confirmedData, setConfirmedData] = useState([]);
  const [confirmedOperation, setConfirmedOperation] = useState('save');
  const [groups, setGroups] = useState([]);
  const [revenues, setRevenues] = useState({});
  const [chartData, setCharData] = useState([]);
  const [editedRowIds, setEditedRowIds] = useState(new Set());
  const [invalidRowIds, setInvalidRowIds] = useState(new Set());
  const [emptyDateRowIds, setEmptyDateRowIds] = useState(new Set());
  const [pastDateModalOpen, setPastDateModalOpen] = useState(false);
  const [saveSuccessOpen, setSaveSuccessOpen] = useState(false);
  const [successOperation, setSuccessOperation] = useState('save');
  const [savedAt, setSavedAt] = useState(0);
  const [loading, setLoading] = useState(false);
  const [modalChangesOpen, setModalChangesOpen] = useState(false);

  const viteMode = import.meta.env.VITE_MODE;
  const simuladoresLink = viteMode === "dev" ? "qa" : viteMode === "prod" ? "" : viteMode;
  const verPropostasLink = `https://comercial${simuladoresLink ? `-${simuladoresLink}` : ""}.vcimentos.com/#/imo/clientes?codigo=${cliente?.codigo}`;

  const [showMessageForm, setShowMessageForm] = useState({
    text: "",
    type: "success",
    icon: IconSuccess,
  });

  const isDirty = editedRowIds.size > 0;

  const editedRowIdsRef = useRef(editedRowIds);
  editedRowIdsRef.current = editedRowIds;

  // const invalidRowIdsRef = useRef(invalidRowIds);
  // invalidRowIdsRef.current = invalidRowIds;

  const revertEditedRows = useCallback(async () => {
    const ids = Array.from(editedRowIdsRef.current);
    for (const id of ids) {
      const original = await db.funilObra.get(id);
      if (original) {
        await db.funilObraDashboard.update(id, {
          volumeNegociado: original.volumeNegociado,
          dataNegociacao: original.dataNegociacao,
          funilObraStatusId: original.funilStatusId,
        });
      }
    }
    setEditedRowIds(new Set());
  }, []);

  //RESET TOTAL DOS DADOS, CASO PRECISE
  const resetDashboardToSaved = useCallback(async () => {
    const data = [];
    await db.funilObra
      .where({ clienteId: parseInt(clienteId) })
      .each((e) => data.push(fixObject({ obj: e })));

    await resetFunnel({ data });
  }, [clienteId]);

  useFunnelUnsavedChanges(isDirty, undefined, revertEditedRows);

  const getFunnel = useCallback(async () => {
    let data = [];
    await db.funilObraDashboard.where({ clienteId: parseInt(clienteId) })
      .filter(e => !e.deletadoEm).each(e => data.push(e));

    return data;
  }, [clienteId]);

  const handleRowEdited = useCallback((id) => {
    setEditedRowIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const handleRowReset = useCallback((id) => {
    setEditedRowIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const handleRowInvalid = useCallback(({id, invalid, isDateEmpty = false, isDate = false}) => {
    if(isDate) {
      setEmptyDateRowIds((prev) => {
        if (prev.has(id) === isDateEmpty) return prev;
        const next = new Set(prev);
        if (isDateEmpty) next.add(id);
        else next.delete(id);
        return next;
      });
    }
    
    setInvalidRowIds((prev) => {
      if (invalid && prev.has(id)) return prev;
      if (!invalid && !prev.has(id)) return prev;
      const next = new Set(prev);
      if (invalid) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const handleFunnelRefetch = useCallback(async () => {
    const temp = await getFunnel();
    setFunnel(temp);
  }, [getFunnel]);


  const handleSaveConfirm = useCallback(async () => {
    setEditedRowIds(new Set());
    setSavedAt((prev) => prev + 1);
    setSaveSuccessOpen(false);
  }, [getFunnel]);

  const handleSubmitFunnel = async () => {
    const obj = {
      id: randomId(15),
      type: "funilObra",
      clienteId,
      toSend: confirmedData,
      createdAt: new Date().toISOString(),
      sended: false,
    };

    try {
      const response = await save(obj);

      if (response?.isOffline) {
        if (isDirectRoteirizacao && confirmedOperation === "save") {
          completeVisitFunnel();
          return;
        }
        setShowMessageForm({
          text: `Funil atualizado com sucesso (modo offline)`,
          icon: IconSuccess,
          type: "success",
        });
        return;
      }

      if(response && Array.isArray(response)) {
        const data = response;
        await resetFunnel({ data, table: db.funilObra, clienteId });
        await resetFunnel({ data: data.map(e => fixObject({ obj: e})) });

        if (isDirectRoteirizacao && confirmedOperation === 'save') {
          completeVisitFunnel();
          return;
        }

        handleFunnelRefetch();
        setSaveSuccessOpen(true);
      }
    } catch (error) {
      const message = error?.message ?? "";
      setShowMessageForm({
        text: `Ocorreu um erro durante o envio do funil - ${message ?? ""}`,
        icon: WarningIcon2,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (confirmedData?.length) {
      setLoading(true);

      handleSubmitFunnel();
    } else {
      setLoading(false);
    }
  }, [confirmedData]);

  const { funilObraGrupoProdutoObjeto, optionFunilObraGruposProduto } = useFunilObraGrupoProduto({ showAll: true });
  const { funilObraUnidadeMedidaObjeto } = useFunilObraUnidadeMedida({});
  const { optionFunisStatus, funilStatusObjBySlug } = useFunilStatus({});
  const emNegociacaoStatusId = funilStatusObjBySlug?.["em-negociacao"]?.id ?? null;
  const naoNegociadoStatusId = funilStatusObjBySlug?.["nao-negociado"]?.id ?? null;
   
  const funilObrasLive = useLiveQuery(
    () =>
      db.funilObraDashboard
        .where({ clienteId: parseInt(clienteId) })
        .toArray(),
    [clienteId],
    []
  )

  const hasInvalidNegotiations = useMemo(() => {
    if (!funilObrasLive) return false;

    const today = new Date();

    return funilObrasLive
      .filter(item => editedRowIds.has(item.id) && !item.deletadoEm)
      .some(item => isPastNegotiationDate(
        item.dataNegociacao, item.funilObraStatusId, emNegociacaoStatusId, today
      ));
  }, [funilObrasLive, editedRowIds, emNegociacaoStatusId]);

  const getDeviceAndLocation = async () => {
    const dispositivo = getDevice(isTablet, isDesktop, deviceType);
    let pos = await getLocation();
    for (const e of Object.keys(pos)) 
      pos[e] = pos[e] ? pos[e].toString() : null;

    return {
      dispositivo,
      ...pos
    }
  }

  const handleSave = useCallback(async () => {
    if (hasSavedVisitGroup && editedRowIds.size === 0) {
      completeVisitFunnel();
      return;
    }

    if (hasInvalidNegotiations) {
      setPastDateModalOpen(true);
      return;
    }

    let data = [];
    await db.funilObraDashboard.where({ clienteId: parseInt(clienteId) })
      .each(e => data.push(e));

    const extraData = await getDeviceAndLocation();

    setConfirmedOperation('save');
    setConfirmedData(data.filter(e => editedRowIds?.has(e.id)).map(f => ({
      ...f,
      createdAt: formatDateToCustomString(new Date()),
      ultimaAtualizacao: formatDateToCustomString(new Date()),
      funilStatusId: f.funilObraStatusId,
      id: 0,
      ativo: true,
      ...extraData
    })));
  }, [editedRowIds, emNegociacaoStatusId, hasInvalidNegotiations, hasSavedVisitGroup, completeVisitFunnel, navigate, retorno, clienteId]);

  const removeGroup = async (id) => {
    setLoading(true);
    const extraData = await getDeviceAndLocation();
    const date = formatDateToCustomString(new Date());

    let data = [];
    await db.funilObraDashboard.where({ clienteId: parseInt(clienteId) }).filter(e => id.includes(e.id))
    .each(e => {
      data.push({
        ...e,
        deletadoEm: date,
        createdAt: date,
        ultimaAtualizacao: date,
        funilStatusId: e.funilObraStatusId,
        id: 0,
        ativo: true,
        ...extraData
      })
    });

    setConfirmedOperation('delete');
    setConfirmedData(data);
  }

  const handleFilterChange = (field, option) => {
    setFilters((prev) => ({ ...prev, [field]: option }));
  };

  const handleClearFilters = () => {
    setFilters({ grupoProduto: null, orderBy: null });
  };

  const filteredGroups = useMemo(() => {
    let list = groups;
    if (filters.grupoProduto?.value) {
      list = list.filter((g) => g.id === filters.grupoProduto.value);
    }
    return sortGroups(list, filters.orderBy?.value);
  }, [filters, groups]);

  const filteredChartData = useMemo(() => {
    if (!filters.grupoProduto?.value) return chartData;
    return chartData.filter((d) => !d.deletadoEm && d.groupId === filters.grupoProduto.value);
  }, [filters.grupoProduto, chartData]);

  const grupoProdutoOptionsFiltered = useMemo(() => {
    const existingIds = new Set(groups.map((g) => g.id));
    const filtered = optionFunilObraGruposProduto.filter((opt) => existingIds.has(opt.value));
    return [{ label: "Todos", value: null }, ...filtered];
  }, [groups, optionFunilObraGruposProduto]);

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => toggleSelectedId(prev, id));
  };

  const handleConfirm = useCallback(async ({ date } = {}) => {
    if (date) {
      for (const id of selectedIds) {
        await db.funilObraDashboard.update(id, { dataNegociacao: date });
      }

      setEditedRowIds((prev) => {
        const next = new Set([
          ...prev,
          [...selectedIds]
        ]);

        return next;
      });

      const temp = await getFunnel();
      setFunnel(temp);
    }
    setSelectedIds([]);
  }, [selectedIds, getFunnel]);

  const goToEdit = (groupId) => {
    navigate(
      `${initialPath}/funil-de-obras/${clienteId}/editar/${groupId}/grupo?origem=acompanhamento-mapeamento&${new URLSearchParams({ ...(retorno ? { retorno } : {}), from: "direto" })}`
    );
  };

  useEffect(() => {
    (async () => {
      const funnelGroupsByCodigo = new Map(Object.values(funilObraGrupoProdutoObjeto).map(group => [group.codigo, group]));

      const groups = {};

      await db.grupoProdutos.each(group => {
        const funnelGroup = funnelGroupsByCodigo.get(group.codigo);

        if (funnelGroup) {
          groups[group.id] = {
            funnelGroupId: funnelGroup.id
          };
        }
      });

      const data = {};

      await db.faturamentos.where({ clienteId: parseInt(clienteId) })
      .each(faturamento => {
          const group = groups[faturamento.grupoMaterialId];
          addVolumesFaturados(data, faturamento, group, funilObraGrupoProdutoObjeto);
        });

      setRevenues(data);
    })();
  }, [funnel, funilObraGrupoProdutoObjeto, funilStatusObjBySlug, clienteId]);

  useEffect(() => {
    if(funilObraGrupoProdutoObjeto && funilObraUnidadeMedidaObjeto &&
      Object.entries(funilObraGrupoProdutoObjeto)?.length && Object.entries(funilObraUnidadeMedidaObjeto)?.length && funnel) {
        const {graphData, formattedFunnel} = formatFunnelData({funilObraGrupoProdutoObjeto, funilObraUnidadeMedidaObjeto, funilObraStatusObjeto: funilStatusObjBySlug, funil: funnel, revenues});

        setCharData(graphData);
        setGroups(Object.values(formattedFunnel));
      }
  }, [funilObraGrupoProdutoObjeto, funilObraUnidadeMedidaObjeto, funnel, revenues])

  useEffect(() => {
    (async() => {
        if(!origem || origem != origens.acompanhamento_mapeamento) {
          await resetDashboardToSaved();
        }
        const temp = await getFunnel();
        if(temp.length) {
          setEditedRowIds((prev) => {
            const next = new Set(prev);
            //ADICIONA OS DELETADOS E OS NOVOS ITENS (ID == STRING) PARA DISPONIBILIZAR
            //O BOTÃO DE ATUALIZARS/SALVAR
            temp.filter(e => !e.deletadoEm && typeof e.id == 'string').forEach(e => {
              next.add(e.id);
            })
            return next;
          });
          setFunnel(temp);
        } else {
          navigate(`${initialPath}/funil-de-obras/${clienteId}/mapeamento`);
        }
    })()
  }, []);

  const canSave = useMemo(() =>
      filteredGroups.length > 0 &&
      emptyDateRowIds.size === 0 &&
      invalidRowIds.size === 0 &&
      !funilObrasLive.some(item => !item.deletadoEm && isInvalidNegotiatedVolume(
        item.volumeNegociado, item.funilObraStatusId, naoNegociadoStatusId
      )),
  [filteredGroups, emptyDateRowIds, invalidRowIds, funilObrasLive, naoNegociadoStatusId]);

  useConfigureFooter(
    {
      buttonsComponent: (
      (isDirty || hasSavedVisitGroup)
        ? (canSave ? <FunnelMassDateButton label="Salvar" onClick={handleSave} /> : null)
        : isDirectRoteirizacao
          ? <FunnelMassDateButton label="Concluir" onClick={() => setModalChangesOpen(true)} />
          : null
    ),
    },
    [selectedIds.length, isDirty, hasSavedVisitGroup, handleSave, canSave, isDirectRoteirizacao]
  );

  return (
    <>
      {
        loading ? <LoaderFull /> : null
      }
      <ClientHeader dados={cliente} />
      <div className="container">
        <TrackMappingFilter
          grupoProdutoOptions={grupoProdutoOptionsFiltered}
          orderOptions={orderOptions}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          onAdicionarGrupo={() => {
            const params = new URLSearchParams(searchParams);
            params.set("origem", "acompanhamento-mapeamento");
            if (retorno) params.set("retorno", retorno);
            if (isDirectRoteirizacao) params.set("from", "direto");

            navigate(
              `${initialPath}/funil-de-obras/${clienteId}/acompanhamento-mapeamento/novo-grupo?${params.toString()}`,
              { state: navigationState }
            );
          }}
          verPropostasLink={verPropostasLink}
        />
        <MappedVsConsumed data={filteredChartData} />

        <div className="mt-6">
           <div className="flex justify-end items-center mb-2 gap-2">
            <span className="text-sm font-bold text-primaria">Selecionar todas</span>
            <input
              type="checkbox"
              checked={
                funnel.length > 0 && funnel.every((item) => selectedIds.includes(item.id))
              }
              onChange={(e) => {
                const ids = funnel.map((item) => item.id);
                if (e.target.checked) {
                  setSelectedIds(ids);
                } else {
                  setSelectedIds([]);
                }
              }}
              className="w-5 h-5 accent-primaria rounded"
            />
          </div>
          {filteredGroups.length > 0 ? (
            filteredGroups.map((group) => (
              <TrackingGroupCard
                statusOptions={optionFunisStatus}
                key={group.id}
                id={group.id}
                title={group.title}
                volumeMapeado={group.volumeMapeado}
                unidade={group.unidade}
                proximaNegociacao={group.proximaNegociacaoFormatado}
                hasWarning={group.hasWarning}
                items={group.items}
                selectedIds={selectedIds}
                onToggleSelect={handleToggleSelect}
                onEdit={() => goToEdit(group.id)}
                onDelete={() => removeGroup(selectedIds?.length ? selectedIds : [group?.items[0]?.id ?? 0])}
                onRowEdited={handleRowEdited}
                onRowReset={handleRowReset}
                onRowInvalid={handleRowInvalid}
                emNegociacaoStatusId={emNegociacaoStatusId}
                naoNegociadoStatusId={naoNegociadoStatusId}
                savedAt={savedAt}
                onFunnelRefetch={handleFunnelRefetch}
              />
            ))) : (
            <div className="relative my-4 ml-1">
              <div
                className="absolute left-0 top-3 bottom-3 w-1.5 bg-[#F59E0B] rounded-full"
                style={{ zIndex: 0 }}
              />
              <div
                className="relative flex items-center justify-center gap-8 bg-[#fbf6ea] rounded-xl px-8 py-4 ml-1"
                style={{ zIndex: 1 }}
              >
                <WarningIcon2 fill="#F59E0B" width="40" height="36" />
                <p className="text-sm font-bold text-[#01054C]">
                  Nenhum mapeamento encontrado, adicione ao menos um grupo de produto para salvar as alterações.
                </p>
              </div>
            </div>
          )
          }
        </div>
      </div>
      <NegotiationUpdateModal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        onConfirm={handleConfirm}
        targetIds={selectedIds}
      />

      <ModalAction
        isOpen={pastDateModalOpen}
        onClose={() => setPastDateModalOpen(false)}
        status="alert"
        title="Atenção"
        sub={
          <p className="flex flex-col gap-1 text-[#01054C]">
            <span>Existem negociações com datas anteriores ao mês atual.</span>
            <span>Ajuste a data ou o status desses produtos antes de salvar.</span>
          </p>
        }
        limite
        buttonTwo="OK"
        linkTwo={() => setPastDateModalOpen(false)}
        buttonTwoClassName="px-6 py-3 rounded bg-secundaria text-primaria font-bold border-2 border-secundaria hover:bg-white hover:text-secundaria transition duration-300 ease-in-out"
      />

      {showMessageForm.text ? (
        <MessageForm
          text={showMessageForm.text}
          type={showMessageForm.type}
          showMessage={showMessageForm}
          setShowMessage={() =>
            setShowMessageForm({
              text: "",
            })
          }
          icon={showMessageForm.icon}
        />
      ) : null}

      {saveSuccessOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex items-center justify-center">
          <div className="bg-white px-14 py-14 rounded-[20px] shadow-lg text-center relative">
            <button
              type="button"
              onClick={handleSaveConfirm}
              className="text-gray-500 p-4 rounded-tr-[20px] bg-quinaria absolute top-0 right-0"
            >
              <ModalClosed />
            </button>
            <span className="flex justify-center mb-2">
              <ModalSuccess />
            </span>
            <h3 className="font-bold text-2xl w-[400px] m-auto">
              {successOperation === 'delete' ? (
                'Dados excluídos com sucesso'
              ) : (
                <>Volumes salvos<br />com Sucesso</>
              )}
            </h3>
            <div className="flex justify-center mt-4">
              <button
                type="button"
                onClick={handleSaveConfirm}
                className="px-8 py-3 rounded bg-secundaria text-primaria font-bold border-2 border-secundaria hover:bg-white hover:text-secundaria transition duration-300 ease-in-out"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <ModalAction
        isOpen={modalChangesOpen}
        onClose={() => setModalChangesOpen(false)}
        title="Você não fez nenhuma modificação. Deseja continuar?"
        buttonOne="Sim"
        buttonTwo="Não"
        linkOne={completeVisitFunnel}
        linkTwo={() => setModalChangesOpen(false)}
      />
    </>
  );
};

export default FunnelTrackMapping;
